import { useState, useRef, useEffect, useCallback } from "react";
import { FiPlay, FiPause } from "react-icons/fi";
import styles from "./VoiceMessage.module.css";

const fmt = (s) => {
  if (!s || isNaN(s) || !isFinite(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const VoiceMessage = ({ url, duration: initDuration, isOwn }) => {
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(initDuration || 0);
  const [error,       setError]       = useState(false);
  const [canPlay,     setCanPlay]     = useState(false);

  const audioRef   = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    if (!url) {
      setError(true);
      return;
    }

    // Tear down any previous instance first
    const prev = audioRef.current;
    if (prev) {
      prev.pause();
      prev.src = "";
      audioRef.current = null;
    }

    // Reset state for new URL
    setError(false);
    setCanPlay(false);
    setPlaying(false);
    setCurrentTime(0);

    const audio = new Audio();

    const onError = () => {
      if (!mountedRef.current) return;
      setError(true);
      setPlaying(false);
    };
    const onCanPlay = () => {
      if (!mountedRef.current) return;
      setCanPlay(true);
    };
    const onMeta = () => {
      if (!mountedRef.current) return;
      if (isFinite(audio.duration) && audio.duration > 0)
        setDuration(audio.duration);
    };
    const onUpdate = () => {
      if (!mountedRef.current) return;
      setCurrentTime(audio.currentTime);
    };
    const onEnded = () => {
      if (!mountedRef.current) return;
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("error",          onError);
    audio.addEventListener("canplay",        onCanPlay);
    audio.addEventListener("canplaythrough", onCanPlay);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate",     onUpdate);
    audio.addEventListener("ended",          onEnded);

    audio.preload = "metadata";
    audio.src     = url;
    audioRef.current = audio;

    return () => {
      mountedRef.current = false;
      audio.pause();
      audio.removeEventListener("error",          onError);
      audio.removeEventListener("canplay",        onCanPlay);
      audio.removeEventListener("canplaythrough", onCanPlay);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate",     onUpdate);
      audio.removeEventListener("ended",          onEnded);
      audio.src = "";
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [url]);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || error) return;
    try {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        await audio.play();
        setPlaying(true);
      }
    } catch (e) {
      console.warn("Audio play error:", e.message);
      if (mountedRef.current) { setError(true); setPlaying(false); }
    }
  }, [playing, error]);

  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !duration || error) return;
    const r = e.currentTarget.getBoundingClientRect();
    audio.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * duration;
  }, [duration, error]);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  if (error) {
    return (
      <div className={`${styles.container} ${isOwn ? styles.own : styles.other}`}>
        <div className={styles.errorMsg}>🎤 Voice message unavailable</div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${isOwn ? styles.own : styles.other}`}>
      <button className={styles.playBtn} onClick={toggle} disabled={!canPlay}>
        {playing ? <FiPause size={15} /> : <FiPlay size={15} />}
      </button>
      <div className={styles.middle}>
        <div className={styles.waveform} onClick={handleSeek}>
          {Array.from({ length: 30 }).map((_, i) => {
            const h      = 5 + Math.abs(Math.sin(i * 0.9) * 7 + Math.sin(i * 0.4) * 5);
            const filled = (i / 30) * 100 <= progress;
            return (
              <div key={i}
                className={`${styles.bar} ${filled ? styles.barFilled : ""}`}
                style={{ height: `${Math.max(4, h)}px` }}
              />
            );
          })}
        </div>
        <span className={styles.time}>
          {playing ? fmt(currentTime) : fmt(duration)}
        </span>
      </div>
    </div>
  );
};

export default VoiceMessage;
